import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, TextField, Chip, Divider, CircularProgress, Switch, FormControlLabel } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CancelIcon from '@mui/icons-material/Cancel';
import { Slide } from '../../types/contentSlicing';
import { useLang } from '../../contexts/langContext';
import { extractWrappedLines } from '../../utils/textFormatting';

interface CurrentSlideReviewPanelProps {
  slide: Slide | null;
  text: string;
  loading: boolean;
  onAccept: () => void;
  onRegenerate: (instruction: string) => void;
}

const QUICK_ACTIONS = ['Make it longer', 'Make it shorter', 'More detail', 'Less detail', 'Split into two slides'];
const QUICK_ACTIONS_CN = ['加长', '缩短', '更多细节', '更少细节', '拆分成两张幻灯片'];

const SPECIAL_TYPES = ['title_slide', 'section_title', 'thank_you', 'agenda', 'transition'];

function CurrentSlideReviewPanel({ slide, text, loading, onAccept, onRegenerate }: CurrentSlideReviewPanelProps) {
  const { lang } = useLang();
  const [customInstruction, setCustomInstruction] = useState('');
  const [autoAcceptEnabled, setAutoAcceptEnabled] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [countdownActive, setCountdownActive] = useState(false);

  // Auto-accept countdown logic
  useEffect(() => {
    if (!slide || loading) {
      setCountdownActive(false);
      setCountdown(10);
      return;
    }

    if (autoAcceptEnabled && !countdownActive) {
      setCountdownActive(true);
      setCountdown(10);
    }

    if (countdownActive && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }

    if (countdownActive && countdown === 0) {
      onAccept();
      setCountdownActive(false);
      setCountdown(10);
    }
  }, [slide, loading, autoAcceptEnabled, countdownActive, countdown, onAccept]);

  const handleCancelCountdown = () => {
    setCountdownActive(false);
    setCountdown(10);
  };

  const handleToggleAutoAccept = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setAutoAcceptEnabled(enabled);
    if (!enabled) {
      setCountdownActive(false);
      setCountdown(10);
    }
  };

  if (!slide && !loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          {lang === 'en'
            ? 'No slide to review. Start by entering text and clicking "Start Slicing".'
            : '没有要审阅的幻灯片。请输入文本并点击"开始剪切"。'}
        </Typography>
      </Paper>
    );
  }

  const isSpecialType = SPECIAL_TYPES.includes(slide?.layoutDescription || '');

  // Use generatedContent if available, otherwise extract from line numbers
  const slideText = slide
    ? slide.generatedContent || extractWrappedLines(text, slide.rangeStartLine, slide.rangeEndLine)
    : '';

  const handleQuickAction = (action: string) => {
    onRegenerate(action);
  };

  const handleCustomRegenerate = () => {
    if (customInstruction.trim()) {
      onRegenerate(customInstruction);
      setCustomInstruction('');
    }
  };

  return (
    <Paper sx={{ p: 3, position: 'relative' }}>
      <Typography variant="h6" gutterBottom>
        {lang === 'en' ? 'Current Slide' : '当前幻灯片'}
      </Typography>

      {slide && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
          {slide.rangeStartLine !== -1 && slide.rangeEndLine !== -1 ? (
            <Chip label={`Lines ${slide.rangeStartLine}-${slide.rangeEndLine}`} color="primary" size="small" />
          ) : (
            <Chip label={lang === 'en' ? 'Generated Content' : '生成内容'} color="secondary" size="small" />
          )}
          {isSpecialType ? (
            <Chip label={slide.layoutDescription} color="secondary" size="small" />
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {slide.layoutDescription}
            </Typography>
          )}
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: 200, overflowY: 'auto', overflowX: 'hidden', bgcolor: 'grey.50' }}>
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
        >
          {slideText}
        </Typography>
      </Paper>

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <FormControlLabel
            control={<Switch checked={autoAcceptEnabled} onChange={handleToggleAutoAccept} color="primary" />}
            label={<Typography variant="body2">{lang === 'en' ? 'Auto-accept (10s)' : '自动接受（10秒）'}</Typography>}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={
              countdownActive ? (
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress size={20} sx={{ color: 'rgba(255, 255, 255, 0.3)' }} variant="determinate" value={100} />
                  <CircularProgress
                    size={20}
                    sx={{ color: 'white', position: 'absolute', left: 0 }}
                    variant="determinate"
                    value={(10 - countdown) * 10}
                  />
                </Box>
              ) : (
                <CheckCircleIcon />
              )
            }
            onClick={onAccept}
            fullWidth
          >
            {countdownActive
              ? lang === 'en'
                ? `Accept & Continue (${countdown}s)`
                : `接受并继续（${countdown}秒）`
              : lang === 'en'
              ? 'Accept & Continue'
              : '接受并继续'}
          </Button>
          {countdownActive && (
            <Button variant="outlined" color="warning" startIcon={<CancelIcon />} onClick={handleCancelCountdown} sx={{ minWidth: 140 }}>
              {lang === 'en' ? 'Cancel' : '取消'}
            </Button>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>
        {lang === 'en' ? 'Quick Actions' : '快速操作'}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {QUICK_ACTIONS.map((action) => (
          <Button key={action} variant="outlined" size="small" onClick={() => handleQuickAction(action)}>
            {lang === 'en' ? action : QUICK_ACTIONS_CN[QUICK_ACTIONS.indexOf(action)]}
          </Button>
        ))}
      </Box>

      <Typography variant="subtitle2" gutterBottom>
        {lang === 'en' ? 'Custom Instruction' : '自定义指令'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={lang === 'en' ? 'e.g., "Add more emphasis on key points"' : '例如："在关键点上添加更多强调"'}
          value={customInstruction}
          onChange={(e) => setCustomInstruction(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleCustomRegenerate();
            }
          }}
        />
        <Button
          variant="contained"
          color="ai"
          size="small"
          sx={{ flexShrink: 0 }}
          startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
          onClick={handleCustomRegenerate}
          disabled={!customInstruction.trim() || loading}
        >
          {lang === 'en' ? 'Regenerate' : '重新生成'}
        </Button>
      </Box>

      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            borderRadius: 1,
          }}
        >
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            {lang === 'en' ? 'Generating slide...' : '正在生成幻灯片...'}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default CurrentSlideReviewPanel;
